"use client";

import {
	Image,
	Link,
	Rating,
	type DefaultLinkProps
} from "@pamperedchef/design-system";

import { AddToCartButtonView } from "@/composites/cart/AddToCartButton";

import { PriceDisplay } from "@/composites/product/PriceDisplay";
import {
	ProductHighlightTag,
	type HighlightTag
} from "@/composites/product/ProductHighlightTag";

import * as styles from "./ProductCard.module.scss";

const RATING_SIZE = "sm";

export interface ProductCardImage {
	src: string;
	alt: string;
}

export interface ProductCardPrice {
	display: number;
	strikeThrough?: number;
	currency?: string;
	locale?: string;
}

export interface ProductCardRating {
	value: number;
	count: number;
}

/**
 * A resolved call to action. The caller owns the label and whether the button
 * can be pressed — the card never derives buyability or cart state itself.
 */
export interface ProductCardAction {
	label: string;
	/** Widest label the action can show, so a label swap cannot resize the button. */
	reserveLabel?: string;
	onPress?: () => void;
	isDisabled?: boolean;
}

/**
 * Everything the card draws, already projected by the caller. Declared here
 * rather than taken from a catalog or listing model so any surface that can
 * produce these fields can render the card.
 */
export interface ProductCardProduct {
	sku: string;
	title: string;
	href: string;
	image: ProductCardImage;
	price?: ProductCardPrice;
	rating?: ProductCardRating;
	tags?: HighlightTag[];
}

export interface ProductCardProps {
	product: ProductCardProduct;
	/** Omit to render the card with no button at all. */
	action?: ProductCardAction;
	className?: string;
}

export function ProductCard({
	product,
	action,
	className
}: ProductCardProps): JSX.Element {
	const isPressable = Boolean(action?.onPress) && !action?.isDisabled;

	return (
		<article
			className={[styles["card"], className ?? ""]
				.filter(Boolean)
				.join(" ")}
			data-component="ProductCard"
			data-sku={product.sku}
			data-cta={action ? (isPressable ? "enabled" : "inert") : "none"}
		>
			<a
				href={product.href}
				aria-hidden="true"
				tabIndex={-1}
				className={styles["card__image-link"]}
			>
				<Image
					src={product.image.src}
					alt={product.image.alt}
					aspectRatio="1:1"
					className={styles["card__image"]}
				/>
			</a>

			<div className={styles["card__body"]}>
				<div className={styles["card__details"]}>
					<ProductHighlightTag tags={product.tags ?? []} />

					<Link<DefaultLinkProps>
						href={product.href}
						size="lg"
						className={styles["card__title"]}
					>
						{product.title}
					</Link>

					{product.rating ? (
						<Rating
							rating={product.rating.value}
							reviewCount={product.rating.count}
							size={RATING_SIZE}
						/>
					) : null}

					{product.price ? (
						<PriceDisplay
							display={product.price.display}
							strikeThrough={product.price.strikeThrough}
							currency={product.price.currency}
							locale={product.price.locale}
							size="small"
							className={styles["card__price"]}
						/>
					) : null}
				</div>

				{action ? (
					<AddToCartButtonView
						kind="secondary"
						size="sm"
						label={action.label}
						reserveLabel={action.reserveLabel}
						isDisabled={action.isDisabled}
						onClick={action.onPress}
						showMessages={false}
						className={styles["card__cta"]}
					/>
				) : null}
			</div>
		</article>
	);
}
